import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "react-query";
import axios, { AxiosError } from "axios";
import McmSegment, { McmSegmentListWrapper } from "../model/McmSegment";
import { McmSegmentEmployeeWrapper } from "../model/McmSegmentEmployee";
import McmSegmentListRequest from "../request/McmSegmentListRequest";
import McmSegmentEmployeesRequest from "../request/McmSegmentEmployeesRequest";
import CreateMcmSegmentRequest from "../request/CreateMcmSegmentRequest";
import UpdateMcmSegmentRequest from "../request/UpdateMcmSegmentRequest";
import { HttpClientError } from "../../../../providers/HttpClient";

// --- Segment List ---

export const McmSegmentListApiService = async (
  request: McmSegmentListRequest,
): Promise<McmSegmentListWrapper> => {
  try {
    const url = "/mcm-segments/";
    const { data } = await axios.get(url, { params: request });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useMcmSegmentListApiService = (
  request: McmSegmentListRequest,
) =>
  useQuery<McmSegmentListWrapper, Error | AxiosError>(
    ["McmSegmentListApiService", request.page, request.page_size],
    () => McmSegmentListApiService(request),
    { keepPreviousData: true },
  );

// Fetch-all variant: fetches every page so consumers get the complete segment list.
// Needed by SegmentManagementPanel (accordion grouped by company + client-side search)
// and ComposeForm (segment picker), which both require the full dataset.
const fetchAllMcmSegments = async (
  pageSize: number,
): Promise<McmSegment[]> => {
  const first = await McmSegmentListApiService({
    page: 1,
    page_size: pageSize,
  });
  if (first.pages <= 1) return first.results;

  const remaining = await Promise.all(
    Array.from({ length: first.pages - 1 }, (_, i) =>
      McmSegmentListApiService({ page: i + 2, page_size: pageSize }),
    ),
  );

  return [first, ...remaining].flatMap((r) => r.results);
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useAllMcmSegmentsQuery = (pageSize = 40) =>
  useQuery<McmSegment[], Error | AxiosError>(
    ["McmSegmentListApiService", "all", pageSize],
    () => fetchAllMcmSegments(pageSize),
  );

// --- Segment Employees ---

export const McmSegmentEmployeesApiService = async (
  request: McmSegmentEmployeesRequest,
): Promise<McmSegmentEmployeeWrapper> => {
  try {
    const { segmentId, ...params } = request;
    const url = `/mcm-segments/${segmentId}/employees/`;
    const { data } = await axios.get(url, { params });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useMcmSegmentEmployeesApiService = (
  request: McmSegmentEmployeesRequest,
  enabled = true,
) =>
  useQuery<
    McmSegmentEmployeeWrapper,
    Error | AxiosError,
    McmSegmentEmployeeWrapper
  >(
    [
      "McmSegmentEmployeesApiService",
      request.segmentId,
      request.search,
      request.page,
      request.page_size,
    ],
    () => McmSegmentEmployeesApiService(request),
    { enabled, keepPreviousData: true },
  );

// --- Create Segment ---

export const CreateMcmSegmentApiService = async (
  request: CreateMcmSegmentRequest,
): Promise<McmSegment> => {
  try {
    const url = "/mcm-segments/";
    const { data } = await axios.post(url, request);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

export const useCreateMcmSegmentApiService = (): UseMutationResult<
  McmSegment,
  Error | AxiosError,
  CreateMcmSegmentRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<McmSegment, Error | AxiosError, CreateMcmSegmentRequest>(
    CreateMcmSegmentApiService,
    {
      mutationKey: ["CreateMcmSegmentApiService"],
      onSuccess: () => {
        queryClient.invalidateQueries(["McmSegmentListApiService"]);
      },
    },
  );
};

// --- Update Segment ---

export const UpdateMcmSegmentApiService = async (
  request: UpdateMcmSegmentRequest,
): Promise<McmSegment> => {
  try {
    const { id, ...body } = request;
    const url = `/mcm-segments/${id}/`;
    const { data } = await axios.patch(url, body);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

export const useUpdateMcmSegmentApiService = (): UseMutationResult<
  McmSegment,
  Error | AxiosError,
  UpdateMcmSegmentRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<McmSegment, Error | AxiosError, UpdateMcmSegmentRequest>(
    UpdateMcmSegmentApiService,
    {
      mutationKey: ["UpdateMcmSegmentApiService"],
      onSuccess: () => {
        queryClient.invalidateQueries(["McmSegmentListApiService"]);
      },
    },
  );
};

// --- Delete Segment ---

const DeleteMcmSegmentApiService = async (id: number): Promise<boolean> => {
  try {
    const url = `/mcm-segments/${id}/`;
    const { data } = await axios.delete(url);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useDeleteMcmSegmentApiService = () => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error | AxiosError, number>(
    DeleteMcmSegmentApiService,
    {
      mutationKey: ["DeleteMcmSegmentApiService"],
      onSuccess: () => {
        queryClient.invalidateQueries(["McmSegmentListApiService"]);
      },
    },
  );
};
